<template>
  <div>
    <vxe-grid
      row-id="id"
      ref="tableRef"
      max-height="800"
      resizable
      :sync-resize="visible"
      :columns="tableColumns"
      :data="shopCartTableData"
      @checkbox-all="selectAllChangeHandle"
      @checkbox-change="selectChangeHandle">
      <template #pager>
        <div class="shopcart-grid-pager mt-3 d-flex justify-content-end align-items-center">
          <div v-if="ordersResourceStats" class="shopcart-orders-summary mb-1">
            <template v-if="(workflowData.state || workflowData.process_instance?.state) === 'ACTIVE' && !ordersResourceStats.success">
              {{ $t('scope.shopcart.orders_resource_summary', [ordersResourceStats.total]) }}
            </template>
            <template v-else>
              {{ $t('scope.shopcart.orders_resource_summary_2', [ordersResourceStats.total, ordersResourceStats.success]) }}
            </template>
          </div>
          <div v-if="isOrderSetExecuting" class="shopcart-executing mb-1 ml-3 d-flex align-items-center justify-content-end">
            <a-spin size="small" style="transform: scale(0.8)" />
            <span class="ml-2">{{ $t('scope.shopcart.applying') }}</span>
          </div>
        </div>
      </template>
    </vxe-grid>
    <shop-cart v-if="canUpdate && isServerCreate" component-type="workflowBtn" :orderSetResId="orderSetResId" @update="handleUpdate" />
    <template v-if="policyDiff">
      <div class="mb-4">
        <div class="detail-title">{{ $t('system.added_permissions') }}</div>
        <template v-if="hasActions(policyDiff.addedOptions)">
          <group v-for="service of optionsToList(policyDiff.addedOptions)" :key="`add-${service.key}`" :data="service" />
        </template>
        <div v-else>-</div>
      </div>
      <div class="mb-3">
        <div class="detail-title">{{ $t('system.removed_permissions') }}</div>
        <template v-if="hasActions(policyDiff.removedOptions)">
          <group v-for="service of optionsToList(policyDiff.removedOptions)" :key="`rm-${service.key}`" :data="service" />
        </template>
        <div v-else>-</div>
      </div>
    </template>
  </div>
</template>
<script>
import { sizestr } from '@/utils/utils'
import BrandIcon from '@/sections/BrandIcon'
import { HYPERVISORS_MAP, PROVIDER_MAP } from '@/constants'
import ShopCart from '@scope/views/components/ShopCart'
import Group from '@IAM/views/policy/components/PolicyViewer/Group'
import { getShopCartWorkflowResourceConfigColumn as userGetShopCartWorkflowResourceConfigColumn } from '@IAM/views/user/utils'
import { getShopCartWorkflowResourceConfigColumn as roleGetShopCartWorkflowResourceConfigColumn } from '@IAM/views/role/utils'
import { diffPolicies, getShopCartWorkflowResourceConfigColumn as policyGetShopCartWorkflowResourceConfigColumn } from '@IAM/views/policy/utils'

export default {
  name: 'ShopCarts',
  components: {
    ShopCart,
    Group,
  },
  props: {
    data: {
      type: Array,
    },
    workflowData: {
      type: Object,
      default: () => {},
    },
    /** resource_order_sets 返回的 orders，一条为一类资源执行结果 */
    orders: {
      type: Array,
      default: () => [],
    },
    /** resource_order_sets.status，executing 时展示申请中文案与 loading */
    orderSetStatus: {
      type: String,
      default: '',
    },
    canUpdate: Boolean,
    orderSetResId: String,
  },
  data () {
    return {
      shopCartTablePage: {
        total: 0,
        currentPage: 1,
        pageSize: 100,
      },
      shopCartTableData: [],
    }
  },
  computed: {
    resource () {
      const { resource } = this.data.length ? this.data[0] : {}
      return resource === 'rolepolicies' ? 'roles' : resource
    },
    actionType () {
      const { resource, action, perform_action_name } = this.data.length ? this.data[0] : {}
      return action === 'perform' ? `${resource}_${action}_${perform_action_name}`.replace('-', '_') : `${resource}_${action}`
    },
    isServerCreate () {
      return this.actionType === 'servers_create'
    },
    isServerChangeConfig () {
      return this.actionType === 'servers_perform_change_config'
    },
    isOrderSetExecuting () {
      return this.orderSetStatus === 'executing'
    },
    /** orders 按 set_parameter_id 索引，与 parameters.id 对齐 */
    orderByParameterId () {
      const map = {}
      ;(this.orders || []).forEach((order) => {
        if (order?.set_parameter_id) {
          map[order.set_parameter_id] = order
        }
      })
      return map
    },
    tableColumns () {
      // 新建主机 / 调整配置： servers_create / servers_perform_change_config
      if (this.isServerCreate || this.isServerChangeConfig) {
        const ret = [
          {
            field: 'resource',
            title: this.$t('common.resource_type'),
            minWidth: 140,
            slots: {
              default: ({ row }) => {
                let resourceLabel = this.$t(`dictionary.${row.resource.slice(0, -1)}`)
                if (row.resource === 'servers' && row.parameter?.extraData?.__resource_type__) {
                  resourceLabel = this.$t(`dictionary.${row.parameter.extraData.__resource_type__}`)
                }
                const opLabel = (row.action === 'perform' && row.perform_action_name === 'change-config')
                  ? this.$t('scope.shopcart.op_type.change_config')
                  : this.$t('scope.shopcart.op_type.apply')
                return [`${resourceLabel}-${opLabel}`]
              },
            },
          },
          {
            field: 'name',
            title: this.$t('common.name'),
            minWidth: 100,
            slots: {
              default: ({ row }) => {
                return [
                  row.parameter?.generate_name,
                ]
              },
            },
          },
          {
            field: 'instance_type',
            title: this.$t('table.title.flavor'),
            showOverflow: 'ellipsis',
            minWidth: 140,
            sortable: true,
            slots: {
              default: ({ row }) => {
                if (row.action === 'perform' && row.perform_action_name === 'change-config') {
                  const before = row.parameter?.config_before || '-'
                  const after = row.parameter?.config_after || (() => {
                    const { disks = [], vcpu_count, vmem_size } = row.parameter || {}
                    const disk = disks.map(o => o.size).reduce((acc, cur) => acc + cur, 0)
                    return vcpu_count + 'C' + (vmem_size / 1024) + 'G' + (disk ? sizestr(disk, 'M', 1024) : '')
                  })()
                  return [
                    <div style={{ color: '#53627C', lineHeight: '1.4' }}>
                      <div>{ this.$t('scope.shopcart.config_before', [before]) }</div>
                      <div>{ this.$t('scope.shopcart.config_after', [after]) }</div>
                    </div>,
                  ]
                }
                const parameter = row.parameter || {}
                const resource = parameter.extraData?.__resource_type__ || (row.resource || '').replace(/s$/, '')
                if (resource === 'baremetal') {
                  const spec = parameter.extraData?.specifications || '-'
                  const fromBmImport = parameter.fake_create_from_bm_import ?? parameter.extraData?.fake_create_from_bm_import
                  const on = fromBmImport === true || fromBmImport === 'true' || fromBmImport === 1
                  return [
                    <div class='text-truncate' style={{ color: '#53627C', lineHeight: '1.4' }}>
                      <div>{ spec }</div>
                      { on ? <div>{ this.$t('compute.server_package.fake_create_from_bm_import') }: { this.$t('table.title.on') }</div> : null }
                    </div>,
                  ]
                }
                const { disks = [], vcpu_count, vmem_size } = parameter
                const disk = disks.map(o => o.size).reduce((acc, cur) => acc + cur, 0)
                const config = vcpu_count + 'C' + (vmem_size / 1024) + 'G' + (disk ? sizestr(disk, 'M', 1024) : '')
                return [
                  <div class='text-truncate' style={{ color: '#53627C' }}>{ config }</div>,
                ]
              },
            },
          },
          {
            field: 'hypervisor',
            title: this.$t('table.title.brand'),
            width: 60,
            slots: {
              default: ({ row }, h) => {
                let { hypervisor, provider } = row.parameter || {}
                if (!hypervisor) return '-'
                // 裸金属 / 容器主机：优先 provider，其次 formType
                if (hypervisor === 'baremetal' || hypervisor === 'pod') {
                  let brand = ''
                  if (provider) {
                    brand = PROVIDER_MAP[provider]?.brand || provider
                  }
                  if (!brand) {
                    const formType = row.parameter.extraData?.formType
                    if (formType === 'private') brand = 'Cloudpods'
                    else if (formType === 'onpremise' || formType === 'idc') brand = 'OneCloud'
                  }
                  if (!brand) return '-'
                  return [
                    <BrandIcon name={brand} />,
                  ]
                }
                if (row.parameter.extraData?.formType === 'private' && hypervisor === 'kvm') {
                  hypervisor = 'cloudpods'
                }
                const brand = HYPERVISORS_MAP[hypervisor]?.brand
                if (!brand) return '-'
                return [
                  <BrandIcon name={brand} />,
                ]
              },
            },
          },
          {
            field: 'billing_type',
            title: this.$t('table.title.bill_type'),
            width: 80,
            slots: {
              default: ({ row }, h) => {
                const resource = row.parameter?.extraData?.__resource_type__ || row.resource.slice(0, -1)
                if (resource === 'baremetal' || resource === 'server_container') {
                  return '-'
                }
                const ret = []
                const { billing_type = 'postpaid' } = row.parameter
                if (billing_type === 'postpaid') {
                  ret.push(<div style={{ color: '#0A1F44' }}>{ this.$t('billingType.postpaid') }</div>)
                } else if (billing_type === 'prepaid') {
                  ret.push(<div style={{ color: '#0A1F44' }}>{ this.$t('billingType.prepaid') }</div>)
                }
                return ret
              },
            },
          },
          { field: 'count', minWidth: 50, title: this.$t('scope.text_838') },
          {
            field: 'apply_progress',
            title: this.$t('scope.shopcart.apply_success'),
            minWidth: 90,
            slots: {
              default: ({ row }) => {
                const order = this.orderByParameterId[row.id]
                if (!order) return '-'
                return Number(order.success_count) || 0
              },
            },
          },
          {
            field: 'price',
            title: this.$t('common_419'),
            minWidth: 200,
            slots: {
              default: ({ row }, h) => {
                const resource = row.parameter?.extraData?.__resource_type__ || row.resource.slice(0, -1)
                if (resource === 'baremetal' || resource === 'server_container') {
                  return '-'
                }
                const { price } = row.parameter
                if (!price) return '-'
                const idx = price.indexOf('(')
                return [
                  <span class='emphasize'>{ price.substring(0, idx) }</span>,
                  <span class='second'>{ price.substring(idx) }</span>,
                ]
              },
            },
          },
          {
            field: '_operation',
            title: this.$t('table.title._action'),
            width: 120,
            slots: {
              default: ({ row, ...rets }, h) => {
                const ret = [<a-button class="m-0 p-0 mr-1" type='link' onClick={ () => { this.$emit('view', row, rets) } }>{ this.$t('common.view') }</a-button>]
                if (this.canUpdate) {
                  ret.push(<a-button class="m-0 p-0 mr-1" type='link' disabled={ row.resource !== 'servers' || row.action !== 'create' } onClick={ () => { this.$emit('edit', row, rets) } }>{ this.$t('common.edit') }</a-button>)
                  ret.push(<a-popconfirm
                    placement="topRight"
                    title={ this.$t('common.delete_resource_from_shopcart_tip') }
                    ok-text={ this.$t('common.ok') }
                    cancel-text={ this.$t('common.cancel') }
                    onConfirm={ () => { this.$emit('delete', row, rets) } }>
                    <a-button class="m-0 p-0" type='link' disabled={ this.data.length <= 1 }>{ this.$t('common.delete') }</a-button>
                  </a-popconfirm>)
                }
                return [
                  <div class='d-flex align-items-center'>
                    { ...ret }
                  </div>,
                ]
              },
            },
          },
        ]
        return ret
      } else {
        const list = [
          {
            field: 'resource',
            title: this.$t('common.resource_type'),
            width: 100,
            slots: {
              default: ({ row }) => {
                let resource = row.resource
                if (row.resource === 'rolepolicies') {
                  resource = 'roles'
                }
                if (row.resource === 'policies') {
                  resource = 'policys'
                }
                return [
                  this.$t(`dictionary.${resource.slice(0, -1)}`),
                ]
              },
            },
          },
          {
            field: 'name',
            title: this.$t('common.name'),
            width: 100,
            formatter: ({ row }) => {
              if (row.resourceData) {
                return row.resourceData.name
              } else {
                return row.resource_id || '-'
              }
            },
          },
          {
            field: 'action',
            title: this.$t('common.operation_type'),
            width: 120,
            formatter: ({ row }) => {
              return this.$t(`system.shopcart.action_type.${this.actionType}`)
            },
          },
        ]
        if (this.resource === 'users') {
          list.push(userGetShopCartWorkflowResourceConfigColumn())
        }
        if (this.resource === 'roles' || this.resource === 'rolepolicies') {
          list.push(roleGetShopCartWorkflowResourceConfigColumn())
        }
        if (this.resource === 'policies') {
          list.push(policyGetShopCartWorkflowResourceConfigColumn())
        }
        return list
      }
    },
    // 修改之前的权限
    originPolicy () {
      const { resource, action, resourceData } = this.shopCartTableData.length ? this.shopCartTableData[0] : {}
      if (resource === 'policies' && action === 'update') {
        return resourceData?.policy?.policy || null
      }
      return null
    },
    // 修改之后的权限
    newPolicy () {
      const { resource, action, parameter } = this.data.length ? this.data[0] : {}
      if (resource === 'policies' && action === 'update') {
        return parameter?.policy?.policy || null
      }
      return null
    },
    policyDiff () {
      if (this.originPolicy && this.newPolicy) {
        return diffPolicies(this.originPolicy, this.newPolicy)
      }
      return null
    },
    /** 基于 orders：汇总 count 为总数、success_count 为成功数 */
    ordersResourceStats () {
      const list = Array.isArray(this.orders) ? this.orders : []
      if (!list.length) return null
      const total = list.reduce((sum, o) => sum + (Number(o?.count) || 0), 0)
      const success = list.reduce((sum, o) => sum + (Number(o?.success_count) || 0), 0)
      const fail = total - success
      return { total, success, fail }
    },
  },
  watch: {
    data: {
      handler (v) {
        this.shopCartTableData = v.length ? v.map(item => {
          if (item.resource === 'rolepolicies') {
            return {
              ...item,
              resource_id: item.batch_resource_ids.map(id => id.split(':')[0])[0],
            }
          }
          return item
        }) : []
        this.shopCartTablePage.total = v?.length || 0
      },
      deep: true,
      immediate: true,
    },
  },
  created () {
    if (this.resource !== 'servers') {
      this.fetchResourceList()
    }
  },
  methods: {
    hasActions (options = {}) {
      return Object.values(options || {}).some(service => {
        return Object.values(service.children || {}).some(resource => (resource.actions || []).length)
      })
    },
    optionsToList (options = {}) {
      return Object.values(options || {})
    },
    async fetchResourceList () {
      const res = await new this.$Manager(this.resource).list({
        params: {
          scope: this.$store.getters.scope,
          filter: `id.in(${this.shopCartTableData.map(item => `'${item.resource_id}'`).join(',')})`,
        },
      })
      const resourceList = res.data?.data || []
      this.shopCartTableData = this.shopCartTableData.map(item => {
        const resource = resourceList.find(r => r.id === item.resource_id)
        return {
          ...item,
          resourceData: resource,
        }
      })
    },
    handleUpdate (data) {
      this.$emit('refresh', data)
    },
  },
}
</script>

<style lang="scss">
.emphasize {
  color: #f5222d;
  font-size: 16px;
}
.second {
  color: #999;
  font-size: 12px;
}
.shopcart-grid-pager {
  .shopcart-orders-summary {
    font-size: 14px;
    text-align: right;
  }
  .shopcart-executing {
    font-size: 12px;
    color: #53627c;
  }
}
</style>
